<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Return"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-text-field v-model="productReturn.customer.name" outlined dense disabled label="Customer" :hint="customerHint" persistent-hint/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-text-field label="Email" v-model="productReturn.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Bill to address" v-model="productReturn.billToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" md="5" offset-sm="1">
              <v-row dense>
                <v-col cols="6">
                  <date-picker v-model="productReturn.date" label="Return date/time" @change="changeDate()"/>
                </v-col>
              </v-row>
              <v-row v-if="sites.length>1" dense>
                <v-col>
                  <v-autocomplete v-model="productReturn.siteId" outlined dense label="Return to site" @change="changeSite()"
                                  :items="sites" item-text="name" item-value="id"/>
                </v-col>
              </v-row>

              <v-row v-if="connectedToAccounting">
                <v-col cols="10" lg="8">
                  <v-checkbox label="Create credit memo in QuickBooks" v-model="productReturn.qbo"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col class="text-h6 text-right pr-6" v-if="$vuetify.breakpoint.mdAndUp">
              Amount {{this.company.settings.homeCurrency !== productReturn.customer.currency ? productReturn.customer.currency : ''}} {{total | currency}}
              <div class="text-body-1" v-if="productReturn.number">#{{productReturn.number}}</div>
            </v-col>

          </v-row>

          <v-row dense v-if="values">
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="productReturn.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="productReturn.lines" :rules="[lines]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="productReturn.lines" item-key="key" no-data-text="There must be at least one item to return"
                              :headers="lineHeaders" :expanded.sync="expanded"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <div class="pl-1">{{item.type}}: {{itemName(item)}} {{item.type==='Product' ? (item.inBondProduct ? ' (in-bond)' : ' (duty-paid)') : ''}}</div>
                    <template v-if="selectedItem === item.key">
                      <template v-if="item.type==='Product' && allowInBond">
                        <v-checkbox dense v-if="selectedItem === item.key" v-model="item.inBondProduct" class="mt-4">
                          <template v-slot:label><span class="text-caption">Return product to in-bond inventory</span></template>
                        </v-checkbox>
                      </template>
                      <template v-else-if="item.type==='Spirit' || item.type==='Wine' || item.type==='NonExcise'">
                        <v-container class="pa-0 mt-4">
                          <v-row dense>
                            <v-col cols="8">
                              <tank-select v-model="item.tankId" :tanks="tanks" :hint="returnId===undefined" :required="false" outlined label="Return to"/>
                            </v-col>
                            <v-col cols="4">
                              <v-checkbox dense v-model="item.importedAlcohol" label="Imported" hide-details v-if="item.type !== 'NonExcise'">
                                <template v-slot:label><span class="mt-n1 text-caption">Imported</span></template>
                              </v-checkbox>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                    </template>
                    <div v-else-if="item.type==='Spirit' || item.type==='Wine' || item.type==='NonExcise'" class="pl-1 text-caption">
                      {{tankName(item.tankId)}}
                    </div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Quantity"
                                             v-model="item.quantity" prefix=""
                                            :suffix="suffix(item)" :precision="precision(item)" class="text-caption" @change="changeQuantityPrice(item)"
                                            :no-minimum="item.type==='Item'"/>
                      <v-checkbox v-if="item.type==='Product'" dense v-model="item.cases" class="mt-n4" hide-details>
                        <template v-slot:label><span class="text-caption">cases</span></template>
                      </v-checkbox>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                    </div>
                    <div v-if="lotTracking(item)" class="text-caption">Lot: {{item.lot}}</div>
                  </template>
                  <template v-slot:item.price="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(item)"/>
                      <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">${{item.unitPrice | number(4)}}</div>
                    </template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)" :no-minimum="item.type==='Item'"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :rules="[connectedToAccounting?fieldRules.required:true]"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                      <div v-else class="pl-1" :class="{'error--text':connectedToAccounting && !item.taxCodeId}">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:expanded-item="{headers,item}">
                    <td :colspan="headers.length" class="pa-0">
                      <v-card flat>
                        <v-simple-table dense class="text-caption sblue-grey lsighten-5" style="width:100%">
                          <tr v-for="bundleItem in item.items" :key="bundleItem.key">
                            <td :width="headers[0].width" :min-width="headers[0].width"/>
                            <td :width="headers[1].width" :min-width="headers[1].width" class="px-4">
                              <v-text-field dense v-model="bundleItem.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption"/>
                            </td>
                            <td :width="headers[2].width" :min-width="headers[2].width" class="px-4 text-caption">
                              {{item.quantity}} x {{bundleItem.quantity | number(precision(bundleItem))}} {{suffix(bundleItem)}}
                            </td>
                            <td :width="headers[3].width" :min-width="headers[3].width" class="px-4">
                              <decimal-number-input :outlined="false" label="" v-model="bundleItem.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(bundleItem, item)"/>
                            </td>
                            <td :width="headers[4].width" :min-width="headers[4].width" class="px-4">
                              <decimal-number-input :outlined="false" label="" v-model="bundleItem.amount" class="text-caption" @change="changeAmount(bundleItem)" :no-minimum="bundleItem.type==='Item'"/>
                            </td>
                            <td :width="headers[5].width" :min-width="headers[5].width" class="px-4">
                              <v-autocomplete dense label="" v-model="bundleItem.taxCodeId" :rules="[fieldRules.required]"
                                        :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                            </td>
                          </tr>
                        </v-simple-table>
                      </v-card>
                    </td>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to customer" v-model="productReturn.memo" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Private note" rows="2"
                                  v-model="productReturn.privateNote" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row v-if="company.settings.homeCurrency !== productReturn.customer.currency && values">
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3">
                    <decimal-number-input class="text-caption" :label="`1 ${productReturn.customer.currency} =`" outlined v-model="productReturn.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2" v-if="values">
              <v-container class="pa-0">
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <v-row dense align="center" v-if="productReturn.discount.amount">
                  <v-col cols="5" md="4" offset-md="2">
                    <v-select v-model="productReturn.discount.percent" :items="[{text:'Discount percent',value:true},{text:'Discount value',value:false}]"
                              dense outlined hide-details class="text-caption"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="3" md="2">
                    <decimal-number-input v-model="productReturn.discount.amount"
                              :prefix="productReturn.discount.percent?'':'$'" :suffix="productReturn.discount.percent?'%':''"
                              :max="productReturn.discount.percent?100:undefined"
                              hide-details caption
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="4" class="text-right pr-6">{{discountValue?'-':''}}{{discountValue | currency}}</v-col>
                </v-row>
                <template v-for="tax of productReturn.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{productReturn.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{company.settings.homeCurrency !== productReturn.customer.currency ? `(${productReturn.customer.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="company.settings.homeCurrency !== productReturn.customer.currency">
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*productReturn.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-switch v-if="returnId && company.permissions.includes('selling.write')"
                  v-model="edit" label="Edit" hide-details class="mt-0"
                  :disabled="dirty"/>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save(false)">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top}
</style>

<script>
import {format, add} from 'date-fns'
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import customerTypes from '@/mixins/customerTypes'
import TankSelect from '@/components/TankSelect'

export default {
  name: 'SalesReturnsEdit',
  mixins: [confirmLeave,customerTypes],
  components: {TankSelect},
  props: {
    returnId: {type:String, required:false},
    shipmentId: {type:String, required:false},
  },
  data() {
    return {
      edit: false,
      taxes: [],

      spirits: [],
      wines: [],
      nonExcises: [],
      allBulkContainers: [],
      allTanks: [],
      items: [],
      bundles: [],
      products: [],
      customerTypes: [],
      productReturn: {billToAddress:'',shipmentId:'',customer:{}, exchangeRate:1,
                      date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                      siteId:'',lines:[],taxLines:[],memo:'',privateNote:'',qbo:true,
                      discount:{percent:true,amount:0}},
      shipmentInput: null,
      dirty: false,
      saving: false,
      loading: true,
      valid: true,
      expanded: [],
      moveOffset: 0,
      subTotal: 0,
      selectedItem: null,
      lines: v => v.length>0 || 'There must be at least one line item',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    sites() {
      return this.$store.getters.sites(!this.returnId);
    },
    customerHint() {
      const noType = this.productReturn.customer?.type === 'None';
      return `${noType ? '' : `Type: ${this.customerTypes.find(type => type.value === this.productReturn.customer?.type)?.text}`}${this.productReturn.customer?.category?.id ? `${noType?'': ', '}Category: ${this.productReturn.customer.category.name}` : ''}`;
    },
    allowInBond() {
      return this.customerTypes.find(type => type.value === this.productReturn.customer.type)?.prefInBond ?? false
    },
    tanks() {
      return [{id:'',name:'Create new bulk container'},{divider:true},
              ...this.allTanks?.filter(tank => tank.contents.siteId === this.productReturn.siteId)];
    },
    addOkay() {
      return this.types.length;
    },
    values() {
      return this.company.permissions.includes('values');
    },
    lineHeaders() {
      const headers =  [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                        { text: 'Item', value: 'item', width:'30%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                        { text: 'Quantity', value: 'quantity' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Unit price', value: 'price' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Amount', value: 'amount' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                        { text: 'Tax', value: 'taxes' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true}];
      return headers;
    },
    actualSubtotal() {
      return this.productReturn.taxInclusive
              ? this.$roundNumber(this.productReturn.lines.reduce((total,line) =>
                    total + line.amount/ (1+(this.taxes.find(tax => tax.id === line.taxCodeId)?.salesRate??0)/100), 0))
              : this.subTotal;
    },
    discountValue() {
      if (this.productReturn.discount.percent)
        return this.$roundNumber(this.actualSubtotal * this.productReturn.discount.amount/100);
      return this.productReturn.discount.amount;
    },
    total() {
      return this.actualSubtotal - this.discountValue + this.productReturn.taxLines.reduce((total,tax) => tax.amount + total, 0);
    }
  },
  watch: {
    shipment: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    const data = await Promise.all([
      this.$store.dispatch('products'),
      this.$store.dispatch('items'),
//      this.$lazyListLoad(this.products, 'products2', 'id,name', 100, !this.returnId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.bundles, 'bundles2', 'id,name,price,inBondPrice,tax{id},sellingDescription,sku,displayComponents', 100, !this.returnId ? [{field:'active', comparator:'eq', value:'true'}] : []),
//      this.$lazyListLoad(this.items, 'items2', 'id,name,lotTracking,trackUnits,precision', 100, [{field:'sold', comparator:'eq', value:'true'}, ...(!this.returnId ? [{field:'active', comparator:'eq', value:'true'}] : [])]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.returnId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.returnId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.returnId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,groupTaxes', 100, this.returnId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'salesRate', comparator:'ne', value:''}])
    ]);
    this.products = this.returnId ? data[0] : data[0].filter(product => product.active);
    this.items = this.returnId ? data[1].filter(item => item.sold) : data[1].filter(item => item.sold && item.active);
    const results = await this.$apiCall(`query SaleReturnsEdit($edit:Boolean!, $id:ID!, $shipment:Boolean!, $shipmentId:ID!) {
                                              productReturn(id:$id) @include(if:$edit) {
                                                id,shipmentId,billToAddress,email,date,memo,privateNote,shipmentId,taxInclusive,exchangeRate
                                                siteId,number
                                                customer {name,id,type,currency}
                                                qbo
                                                lines {
                                                  type,id,inBondProduct,cases,description,sku,quantity,lot,unitPrice,amount,taxCodeId,importedAlcohol,tankId
                                                  items {type,id,inBondProduct,cases,description,sku,quantity,lot,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                discount {percent, amount}
                                              }
                                              shipment(id:$shipmentId) @include(if:$shipment) {
                                                customer {name,id,type,currency},date
                                                billToAddress,email,site{id}
                                                invoice {taxInclusive,exchangeRate,discount {percent, amount}}
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,unitPrice,amount,taxCodeId,lot
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,unitPrice,amount,taxCodeId,lot}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                              }
                                              salesPreferences {
                                                defaultMessage
                                                defaultQBO,defaultTaxInclusive
                                              }
                                          }`,
                                        {edit:!!this.returnId,
                                         id:this.returnId ?? '',
                                         shipment:this.shipmentId!=undefined,shipmentId:this.shipmentId ?? ''}
                                         );
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    await this.getTanks();      // Refresh list of tanks

    let key = Date.now();
    if (this.returnId) {
      let key = Date.now();
      results.data.productReturn.lines.forEach(item => item.key = key++);
      this.productReturn = results.data.productReturn;
      for (const line of this.productReturn.lines) {
        line.key = key++;
        line.tankId = line.tankId ?? '';
        if (this.productReturn.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.productReturn.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
    }
    else if (this.shipmentId) {
      this.edit = true;
      this.productReturn.shipmentId = this.shipmentId;
      const shipment = results.data.shipment;
      this.productReturn.taxInclusive = shipment.invoice?.taxInclusive ?? results.data.salesPreferences.defaultTaxInclusive;
//      this.productReturn.exchangeRate = shipment.invoice?.exchangeRate ?? 1;
      this.productReturn.customer = shipment.customer;
      this.productReturn.billToAddress = shipment.billToAddress;
      this.productReturn.email = shipment.email;
      this.productReturn.memo = results.data.salesPreferences.defaultMessage;
      this.productReturn.qbo = this.connectedToAccounting && results.data.salesPreferences.defaultQBO;
      this.productReturn.siteId = shipment.site.id;
      this.productReturn.discount = shipment.invoice?.discount ?? {percent:true,amount:0};
      const date = format(add(new Date(shipment.date), {seconds:-1}), 'yyyy-MM-dd HH:mm:ss');
      for (const line of shipment.lines) {
        if (line.type === 'Bundle')   // Not yet supported
          continue;
        line.tankId = '';
        line.importedAlcohol = false;
        if (line.type === 'BulkContainer' || line.type === 'Tank') {
          let alcoholResults;
          if (line.type === 'BulkContainer')
             alcoholResults = await this.$apiCall(`query SalesReturnEdit($id:ID!, $date:DateTime!) {
                                                    bulkContainer(id:$id) {
                                                      contents(date:$date) {alcoholId,alcoholType,imported}
                                                    }
                                                  }`, {id:line.id, date});
          else
            alcoholResults = await this.$apiCall(`query SalesReturnEdit($id:ID!, $date:DateTime!) {
                                                    tank(id:$id) {
                                                      contents(date:$date) {alcoholId,alcoholType,imported}
                                                    }
                                                  }`, {id:line.id, date});
          if (!alcoholResults.error) {
            const contents = alcoholResults.data[line.type === 'BulkContainer' ? 'bulkContainer' : 'tank'].contents;
            line.type = contents.alcoholType === 'Spirit' ? 'Spirit' : (contents.alcoholType === 'NonExcise' ? 'NonExcise' : 'Wine');
            line.importedAlcohol = contents.imported;
            line.id = contents.alcoholId;
          }
        }

        line.key = key++;
        this.productReturn.lines.push(line);
      }
      this.productReturn.taxLines = shipment.taxLines;
      this.getExchangeRate();
    }
    else {
      this.$store.commit('setError', {type:'error',message:`Must edit or create from shipment`});
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.calculateTotals(!this.productReturn.taxLines);
    this.customerTypes = await this.buildCustomerTypes();
    this.$nextTick(() => {this.dirty = false});
    this.loading = false;
  },

  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        oldIndex -= _self.moveOffset;
        const lineSelected = _self.productReturn.lines.splice(oldIndex, 1)[0];
        _self.productReturn.lines.splice(newIndex, 0, lineSelected);
        _self.buildExpandedList();
      },
      onStart({oldIndex}) {
        let offset = 0;
        for (let index = 0; index < oldIndex-offset; index++) {
          const line = _self.productReturn.lines[index];
          if (_self.expanded.some(e => e.key === line.key))
            offset += 1;
        }
        _self.moveOffset = offset;
        _self.expanded.splice(0, _self.expanded.length);
      }
    })

    if (!this.returnId)
      this.$refs.form.validate();
  },

  methods: {
    async getExchangeRate() {
      if (this.productReturn.customer.currency !== this.company.settings.homeCurrency) {
        const results = await this.$apiCall(`query SalesReturnEdit($code:String!,$date:Date!) {
                                                currencyRate(code:$code,date:$date)
                                              }`,
                                              {code:this.productReturn.customer.currency,date:this.productReturn.date.substring(0,10)});
        if (!results.error)
          this.productReturn.exchangeRate = results.data.currencyRate;
      }
    },
    tankName(tankId) {
      return tankId ? (this.tanks.find(tank => tank.id === tankId)?.name ?? '') : 'Create new bulk container';
    },
    itemTax(item) {
      if (this.connectedToAccounting && !item.taxCodeId)
        return 'Required';
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    itemName(line) {
      return [{type:'Product',list:this.products},
              {type:'Item',list:this.items},
              {type:'Bundle',list:this.bundles},
              {type:'Spirit',list:this.spirits},
              {type:'Wine',list:this.wines},
              {type:'NonExcise',list:this.nonExcises}].find(list => list.type === line.type).list.find(item => item.id === line.id).name;
    },
    calculateTotals(calculateTaxes) {
      this.subTotal = this.productReturn.lines.reduce((total,line) =>
        total + (line.items ? line.items.reduce((total,line) => total+line.amount, 0) : line.amount)
      , 0);

      if (calculateTaxes) {
        this.productReturn.taxLines = [];
        this.productReturn.lines.forEach(line => {
          if (line.items)    // Process bundle items
            line.items.forEach(item => this.calculateTaxesOnLine(item));
          else
            this.calculateTaxesOnLine(line);
        });
      }
    },
    calculateTaxesOnLine(line) {
      const discount = 1 - (this.subTotal ? (this.productReturn.discount.percent ? this.productReturn.discount.amount/100 : this.productReturn.discount.amount / this.subTotal) : 0);
      if (line.taxCodeId) {
        const taxDefinition = this.taxes.find(t => t.id === line.taxCodeId);
        if (!taxDefinition)
            return;
        for (const taxCodeId of taxDefinition.groupTaxes ?? [line.taxCodeId]) {
          let tax = this.productReturn.taxLines.find(tax => tax.id === taxCodeId);
          if (!tax) {
            const taxDefinitionToUse = this.taxes.find(t => t.id === taxCodeId);
            if (!taxDefinitionToUse)
                return;
            tax = {id:taxCodeId,name:taxDefinitionToUse.name,percent:taxDefinitionToUse.salesRate,amount:0,onAmount:0};
            this.productReturn.taxLines.push(tax);
          }
          tax.onAmount += discount * (this.productReturn.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount);
          tax.amount += this.$roundNumber(discount * (this.productReturn.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100));
        }
      }
    },
    lotTracking(line) {
      if (line.type === 'Product')
        return line.inBondProduct ? this.company.settings.lotTrackingForProducts : this.company.settings.lotTrackingForDutyPaid;
      if (line.type === 'Item')
        return this.items.find(item => item.id === line.id)?.lotTracking;
      return false;
    },
    changeSite() {
      this.productReturn.lines = this.productReturn.lines.filter(line => this.types.some(t => t === line.type));
    },
    suffix(line) {
      if (line.type === 'Product') {
        if (line.cases)
          return 'case(s)';
        const product = this.products.find(p => p.id === line.id);
        if (product?.bottlesPerUnit > 1)
          return 'unit(s)';
        if (product?.mixedPack)
          return 'pack(s)';
        if (product?.containerType === 'Can')
          return 'can(s)';
        return 'bottle(s)';
      }
      if (line.type === 'Tank' || line.type === 'BulkContainer') {
        const contents = this.vesselContents(line);
        return contents.alcoholType === 'Spirit' ? 'LAA' : 'litres';
      }
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.trackUnits;
      if (line.type === 'Bundle')
        return '';
      return null;
    },
    vesselContents(line) {
      const vessel = this.tanks.find(tank => tank.id === line.id) || this.bulkContainers.find(bc => bc.id === line.id);
      return vessel.contents;
    },
    precision(line) {
      if (line.type === 'Product')
        return 0;
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.precision;
      if (line.type === 'Bundle')
        return 0;
      return 3;
    },
    deleteLine(index) {
      this.productReturn.lines.splice(index, 1);
      this.buildExpandedList();
      this.calculateTotals(true);
    },

    async changeDate() {
      this.getTanks();      // Refresh list of tanks
      this.getExchangeRate();
    },

    async getTanks() {
      this.allTanks = [];
      await this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,contents{siteId,volume,alcoholType,alcoholId,alcoholName}`, 100, this.returnId?[]:[{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.productReturn.date}]);

      // Check for valid line entries
      for (const line of this.productReturn.lines) {
        if ((line.type.startsWith('Tank') && !this.tanks.some(b => b.id === line.tankId)))
          line.tankId = '';
      }
      this.$nextTick(() => this.$refs.form.validate());
    },

    buildExpandedList() {
      this.expanded.splice(0, this.expanded.length);
      for (const line of this.productReturn.lines) {
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents)
            this.expanded.push(line);
        }
      }
    },
    toggleTaxInclusive() {
      for (const line of this.productReturn.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.productReturn.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.salesRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTotals(true);
    },
    changeQuantityPrice(line, parentLine) {
      if (line.items)
        line.items.forEach(l => this.changeQuantityPrice(l, line));
      else
        line.amount = this.$roundNumber(line.quantity * line.unitPrice * (parentLine?.quantity || 1));
      this.calculateTotals(true);
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity) : 0;
      this.calculateTotals(true);
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line) {
      return  line.quantity < 0 && line.type !== 'Item' ||
              line.unitPrice < 0 ||
              line.amount < 0 && line.type !== 'Item' ||
              (this.connectedToAccounting && this.createInvoice && !line.taxCodeId && !line.items?.length) ||
              line.items?.length && line.items.some(subLine => this.isLineIncomplete(subLine));
    },
    async save() {
      if (!this.$refs.form.validate() || this.productReturn.lines.some(line => this.isLineIncomplete(line))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      // Build request, especially information for all items being returned
      // eslint-disable-next-line no-unused-vars
      const {lines,customer,number, ...input} = this.productReturn;
      input.lines = this.productReturn.lines.map(line => {
        let items;    // Only if this is a bundle with displayed items
        if (line.type === 'Bundle' && line.items) {
          items = line.items.map(bundleLine => (
             {type:bundleLine.type === 'Item' ? bundleLine.type : 'Product',
              inBondProduct:false,cases:false,id:bundleLine.id,sku:bundleLine.sku,description:bundleLine.description,
              quantity:bundleLine.quantity,amount:bundleLine.amount,taxCodeId:bundleLine.taxCodeId}
          ));
        }

        let type = line.type;
        let amount = line.amount;
        if (this.productReturn.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + tax.salesRate/100));
        }
        return {type,inBondProduct:this.allowInBond && (line.inBondProduct??false),cases:line.cases,id:line.id,sku:line.sku,description:line.description,
                importedAlcohol:line.importedAlcohol,tankId:line.tankId,
                quantity:line.quantity,amount,taxCodeId:line.taxCodeId,lot:line.lot,items};
      });
      this.productReturnInput = input;

      this.saving = true;

      const results = await this.$apiCall(`mutation SalesReturnsEdit($input: ProductReturnInput!) {
                                              productReturnCrupdate(input:$input) {id}
                                           }`,
                                          {input:this.productReturnInput})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.returnId?'Updated':'Created'} return from ${this.productReturn.customer.name}`});
      }
    }
  }
}
</script>